import React, { useState } from "react";
import axiosInstance from "../../../../helpers/axiosInstance";
import useAuth from "../../../../hooks/useAuth";
import { dateTime, fullName, money, monthYear } from "../../../../helpers/format";
import { Async, Button, Card, Confirm, Dialog, DownloadButton, Empty, Field, Page, Pill, SectionTitle, useAction, useFetch } from "../../../../shared/ui/ui";

// The fund's own tools that the console lacked and the app has: Jobs with
// every job the server runs, and Reports with the monthly statement.

/** Last month, which is what somebody opening Reports usually wants. */
const lastMonth = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * The monthly balance sheet: pick a month, then download it, email it, or
 * draw it up now rather than waiting for the schedule. A list of what the
 * fund can produce comes from the server, so a new report appears here
 * without a new release.
 */
export const Reports = () => {
  const { organisation } = useAuth();
  const reports = useFetch(`/api/report/${organisation._id}/available`);
  const recipient = useFetch(`/api/report/${organisation._id}/statements/recipient`, { select: (body) => body.to });
  const drawn = useFetch(`/api/report/${organisation._id}/statements?limit=24`);
  const [month, setMonth] = useState(lastMonth());
  const [emailing, setEmailing] = useState(false);
  const [to, setTo] = useState("");
  const { busy, run } = useAction();
  const [year, monthNumber] = month.split("-").map(Number);
  const label = monthYear(new Date(year, monthNumber - 1, 1));
  const exists = (drawn.data || []).some((row) => row.year === year && row.month === monthNumber);

  const drawUp = async () => {
    const ok = await run(() => axiosInstance.post(`/api/report/${organisation._id}/statements/run`, { year, month: monthNumber }), {
      done: (data) => data.message || `${label} is drawn up.`,
    });
    if (ok) drawn.reload();
  };
  const email = async () => {
    const ok = await run(
      () => axiosInstance.post(`/api/report/${organisation._id}/statements/${year}/${monthNumber}/email`, to.trim() ? { to: to.trim() } : {}),
      { done: (data) => data.message || "Sent." }
    );
    if (ok) setEmailing(false);
  };

  return (
    <Page title="Reports" subtitle="The fund's books, month by month.">
      <Async state={reports}>
        {(list) =>
          !list.length ? (
            <Empty title="No reports">This fund has no reports yet.</Empty>
          ) : (
            list.map((report) => (
              <Card key={report.id}>
                <p className="sf-row-title">{report.name}</p>
                <p className="sf-muted">{report.description}</p>
                {report.id === "monthly_statement" ? (
                  <>
                    <Field label="Month">
                      <input type="month" value={month} max={lastMonth()} onChange={(event) => setMonth(event.target.value || lastMonth())} />
                    </Field>
                    <p className="sf-muted">
                      {exists ? `${label} is drawn up.` : `${label} has not been drawn up yet.`}
                      {recipient.data ? ` Emailed to ${recipient.data}.` : " No email address is set for it in Settings."}
                    </p>
                    <div className="sf-chips">
                      <DownloadButton
                        label="Download"
                        fileName={`Statement-${month}.xlsx`}
                        url={`/api/report/${organisation._id}/statements/${year}/${monthNumber}/xlsx`}
                      />
                      <Button onClick={() => setEmailing(true)} disabled={!exists}>
                        ✉ Email
                      </Button>
                      <Button variant="text" busy={busy} onClick={drawUp}>
                        {exists ? "Draw up again" : "Draw it up now"}
                      </Button>
                    </div>
                  </>
                ) : null}
              </Card>
            ))
          )
        }
      </Async>
      {emailing ? (
        <Dialog
          title={`Email ${label}?`}
          onClose={busy ? undefined : () => setEmailing(false)}
          actions={
            <>
              <Button variant="text" onClick={() => setEmailing(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" busy={busy} onClick={email} disabled={!recipient.data && !to.trim()}>
                Send
              </Button>
            </>
          }
        >
          <p className="sf-muted">The workbook is attached. {recipient.data ? `It goes to ${recipient.data} unless you give another address.` : "Give the address to send it to."}</p>
          <Field label="Send to (optional)">
            <input type="email" value={to} placeholder={recipient.data || "name@example.com"} onChange={(event) => setTo(event.target.value)} />
          </Field>
        </Dialog>
      ) : null}
    </Page>
  );
};

const JOB_STATUS = { completed: ["good", "Done"], running: ["warn", "Running"], failed: ["bad", "Nothing done"], skipped: ["", "Skipped"] };

/**
 * The fund's background jobs, and the way to run one by hand — for a member
 * who joined after the 1st, an invoice whose first try failed, a reminder
 * somebody asked for. The list comes from the server, so a new job appears
 * here without a new release. Every run, scheduled ones included, is listed.
 */
export const Jobs = () => {
  const { organisation, can } = useAuth();
  const jobs = useFetch(`/api/jobs/${organisation._id}/available`);
  const runs = useFetch(`/api/jobs/${organisation._id}/history?limit=50`);
  const members = useFetch(`/api/member/${organisation._id}/getallminimalmembers`);
  const [memberFor, setMemberFor] = useState({});
  const [confirming, setConfirming] = useState(null);
  const [result, setResult] = useState(null);
  const { busy, run } = useAction();

  if (!can("jobs")) {
    return (
      <Page title="Jobs">
        <Empty title="Not available">Your role does not include the fund's jobs.</Empty>
      </Page>
    );
  }

  const people = (members.data || []).filter((person) => person.role !== "super-admin");
  const describe = (job) => {
    const memberId = memberFor[job.id];
    const member = people.find((person) => person._id === memberId);
    if (job.scope !== "member") return `This runs ${job.name.toLowerCase()} for the whole fund. ${job.description}`;
    return member
      ? `This runs ${job.name.toLowerCase()} for ${fullName(member)}, which emails them.`
      : `This runs ${job.name.toLowerCase()} for every member the fund bills, which emails each of them.`;
  };

  const start = async () => {
    const job = confirming;
    const memberId = job.scope === "member" ? memberFor[job.id] || undefined : undefined;
    const body = await run(() => axiosInstance.post(`/api/jobs/${organisation._id}/run/${job.id}`, { memberId }));
    setConfirming(null);
    if (body) {
      setResult({ title: body.success === false ? "Nothing happened" : "Done", message: body.message || "The job has run." });
      runs.reload();
    }
  };

  return (
    <Page title="Jobs" subtitle="The fund runs these on a schedule. Run one here only when the schedule has missed somebody.">
      <Async state={jobs}>
        {(list) =>
          !list.length ? (
            <Empty title="No jobs">This fund has no jobs that can be run by hand.</Empty>
          ) : (
            list.map((job) => (
              <Card key={job.id}>
                <p className="sf-row-title">{job.name}</p>
                <p className="sf-muted" style={{ margin: "0.25rem 0" }}>
                  {job.description}
                </p>
                <p className="sf-muted" style={{ margin: "0 0 0.75rem" }}>
                  {job.schedule}
                </p>
                {job.scope === "member" ? (
                  <Field label="Run for">
                    <select value={memberFor[job.id] || ""} onChange={(event) => setMemberFor({ ...memberFor, [job.id]: event.target.value })}>
                      <option value="">Every member the fund bills</option>
                      {people.map((person) => (
                        <option key={person._id} value={person._id}>
                          {fullName(person)}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                <Button variant="primary" onClick={() => setConfirming(job)}>
                  ▶ Run now
                </Button>
              </Card>
            ))
          )
        }
      </Async>

      <SectionTitle title="Recent runs" />
      <p className="sf-muted">Every run the fund has made, its own scheduled ones included.</p>
      <Async state={runs}>
        {(history) =>
          !history.length ? (
            <Empty title="Nothing has run yet">Runs appear here as the fund makes them, whether started by hand or on schedule.</Empty>
          ) : (
            <Card flush>
              {history.map((entry) => {
                const [tone, label] = JOB_STATUS[entry.status] || ["", entry.status];
                const name = jobs.data?.find((job) => job.id === entry.job_name)?.name || entry.job_name;
                return (
                  <div key={entry._id} className="sf-row">
                    <div className="sf-row-main">
                      <p className="sf-row-title">
                        {name}
                        {entry.member_name ? ` · ${entry.member_name}` : ""}
                      </p>
                      <p className="sf-row-sub">
                        {dateTime(entry.started_at)}
                        {entry.message ? ` · ${entry.message}` : ""}
                      </p>
                    </div>
                    <div className="sf-row-end">
                      {entry.amount != null ? <p className="sf-row-title">{money(entry.amount)}</p> : null}
                      <Pill tone={tone}>{label}</Pill>
                    </div>
                  </div>
                );
              })}
            </Card>
          )
        }
      </Async>

      {confirming ? (
        <Confirm title={`Run ${confirming.name.toLowerCase()}?`} confirmLabel="Run it" busy={busy} onCancel={() => setConfirming(null)} onConfirm={start}>
          {describe(confirming)}
        </Confirm>
      ) : null}
      {result ? (
        <Dialog title={result.title} onClose={() => setResult(null)} actions={<Button variant="primary" onClick={() => setResult(null)}>Close</Button>}>
          <p className="sf-muted">{result.message}</p>
        </Dialog>
      ) : null}
    </Page>
  );
};
